import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Calculator, ArrowLeftRight, RefreshCw } from "lucide-react";

const CURRENCIES = ["USD", "EUR", "GBP", "JPY", "CHF", "AUD", "CAD", "NZD"];
const PAIRS = ["EUR/USD", "GBP/USD", "USD/JPY", "AUD/USD", "USD/CAD", "USD/CHF", "NZD/USD", "EUR/JPY", "GBP/JPY"];

function pipInfo(pair) {
  const [, quote] = pair.split("/");
  const pipSize = quote === "JPY" ? 0.01 : 0.0001;
  return { quote, pipSize };
}

const PipCalculator = ({ rates }) => {
  const [pair, setPair] = useState("EUR/USD");
  const [account, setAccount] = useState("USD");
  const [balance, setBalance] = useState("10000");
  const [risk, setRisk] = useState("1");
  const [stop, setStop] = useState("20");

  const result = useMemo(() => {
    const bal = parseFloat(balance) || 0;
    const rk = parseFloat(risk) || 0;
    const sl = parseFloat(stop) || 0;
    if (!bal || !rk || !sl || !rates || !Object.keys(rates).length) return null;
    const { quote, pipSize } = pipInfo(pair);
    const riskAmount = bal * (rk / 100);
    // pip value per standard lot (100k units) in QUOTE currency = pipSize * 100000
    let pipValueQuote = pipSize * 100000;
    // convert quote -> account currency: 1 quote = rates[account]/rates[quote] account
    const rateAcct = account === quote ? 1 : (rates[account] || 1) / (rates[quote] || 1);
    const pipValueAcct = pipValueQuote * rateAcct;
    const lots = riskAmount / (sl * pipValueAcct);
    return {
      pipValue: pipValueAcct,
      lots,
      units: lots * 100000,
      riskAmount,
    };
  }, [pair, account, balance, risk, stop, rates]);

  return (
    <div data-testid="pip-calculator-container" className="card-surface rounded-xl p-6">
      <div className="flex items-center gap-2 mb-5">
        <Calculator className="h-5 w-5 text-brand" />
        <h3 className="font-heading text-lg font-bold">Pip & Risk Calculator</h3>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Pair</Label>
          <Select value={pair} onValueChange={setPair}>
            <SelectTrigger data-testid="pip-calc-pair-select" className="bg-[#0C1017] border-white/10 h-10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0C1017] border-white/10 text-white">
              {PAIRS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Account Ccy</Label>
          <Select value={account} onValueChange={setAccount}>
            <SelectTrigger data-testid="pip-calc-account-select" className="bg-[#0C1017] border-white/10 h-10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0C1017] border-white/10 text-white">
              {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Balance</Label>
          <Input data-testid="pip-calc-balance-input" type="number" value={balance} onChange={(e) => setBalance(e.target.value)} className="bg-[#0C1017] border-white/10 h-10 font-data text-white" />
        </div>
        <div className="space-y-1.5">
          <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Risk %</Label>
          <Input data-testid="pip-calc-risk-input" type="number" value={risk} onChange={(e) => setRisk(e.target.value)} className="bg-[#0C1017] border-white/10 h-10 font-data text-white" />
        </div>
        <div className="space-y-1.5 col-span-2">
          <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Stop Loss (pips)</Label>
          <Input data-testid="pip-calc-stop-input" type="number" value={stop} onChange={(e) => setStop(e.target.value)} className="bg-[#0C1017] border-white/10 h-10 font-data text-white" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-6">
        <div className="rounded-lg bg-[#0C1017] border border-white/8 p-3 text-center">
          <div className="font-data text-[9px] uppercase tracking-widest text-slate-500">Lot Size</div>
          <div data-testid="pip-calc-lot-size-result" className="font-data text-lg font-bold text-brand mt-1">{result ? result.lots.toFixed(2) : "—"}</div>
        </div>
        <div className="rounded-lg bg-[#0C1017] border border-white/8 p-3 text-center">
          <div className="font-data text-[9px] uppercase tracking-widest text-slate-500">Pip Value</div>
          <div className="font-data text-lg font-bold text-white mt-1">{result ? `${result.pipValue.toFixed(2)}` : "—"}</div>
        </div>
        <div className="rounded-lg bg-loss/8 border border-loss/20 p-3 text-center">
          <div className="font-data text-[9px] uppercase tracking-widest text-slate-500">At Risk</div>
          <div data-testid="pip-calc-risk-amount-result" className="font-data text-lg font-bold text-loss mt-1">{result ? `${result.riskAmount.toFixed(0)}` : "—"}</div>
        </div>
      </div>
    </div>
  );
};

const CurrencyConverter = ({ rates, updatedAt, onRefresh }) => {
  const [amount, setAmount] = useState("1000");
  const [from, setFrom] = useState("EUR");
  const [to, setTo] = useState("USD");

  const converted = useMemo(() => {
    const amt = parseFloat(amount) || 0;
    if (!rates || !rates[from] || !rates[to]) return null;
    const rate = rates[to] / rates[from];
    return { rate, result: amt * rate };
  }, [amount, from, to, rates]);

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <div data-testid="currency-converter-container" className="card-surface rounded-xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <ArrowLeftRight className="h-5 w-5 text-brand" />
          <h3 className="font-heading text-lg font-bold">Currency Converter</h3>
        </div>
        <button onClick={onRefresh} className="text-slate-500 hover:text-brand transition-colors" data-testid="currency-refresh-button">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">Amount</Label>
          <Input data-testid="currency-amount-input" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-[#0C1017] border-white/10 h-12 font-data text-lg text-white" />
        </div>

        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1.5">
            <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">From</Label>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger data-testid="currency-from-select" className="bg-[#0C1017] border-white/10 h-11 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0C1017] border-white/10 text-white max-h-64">
                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <button data-testid="currency-swap-button" onClick={swap} className="h-11 w-11 shrink-0 rounded-md border border-white/10 bg-white/5 grid place-items-center hover:border-brand/40 hover:text-brand text-slate-400 transition-colors">
            <ArrowLeftRight className="h-4 w-4" />
          </button>
          <div className="flex-1 space-y-1.5">
            <Label className="font-data text-[10px] uppercase tracking-widest text-slate-500">To</Label>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger data-testid="currency-to-select" className="bg-[#0C1017] border-white/10 h-11 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0C1017] border-white/10 text-white max-h-64">
                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="rounded-lg bg-brand/8 border border-brand/25 p-4">
          <div className="font-data text-[10px] uppercase tracking-widest text-slate-400">{amount || 0} {from} =</div>
          <div data-testid="currency-conversion-result" className="font-data text-3xl font-bold text-white mt-1">
            {converted ? converted.result.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—"} <span className="text-brand text-xl">{to}</span>
          </div>
          <div className="font-data text-[11px] text-slate-500 mt-1">1 {from} = {converted ? converted.rate.toFixed(4) : "—"} {to}</div>
        </div>
        <p data-testid="currency-rate-timestamp" className="font-data text-[10px] text-slate-600">Live rate · updated {updatedAt || "—"}</p>
      </div>
    </div>
  );
};

export const ForexToolsPanel = () => {
  const [rates, setRates] = useState({});
  const [updatedAt, setUpdatedAt] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/tools/rates");
      setRates(data.rates || {});
      setUpdatedAt(data.updated_at || "");
    } catch (e) { console.error("Failed to load forex rates:", e); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="forex-tools-panel">
      <PipCalculator rates={rates} />
      <CurrencyConverter rates={rates} updatedAt={updatedAt} onRefresh={load} />
    </div>
  );
};
